import { Component, effect, Signal } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { GenericComponent } from '../generic/generic.component';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { SidenavService } from 'src/app/shared/services/sidenav.service';
import { Store } from '@ngxs/store';
import { ConcessioncontrolState, PrepareDataFromKeys } from '../../states/concessioncontrol.state';
import { combineLatest, distinctUntilChanged } from 'rxjs';
import { CC_CONSTANTS } from '../../assets/constants';
import {
    ApexAxisChartSeries,
    ApexDataLabels,
    ApexGrid,
    ApexOptions,
    ApexPlotOptions,
    ApexYAxis,
    ApexAnnotations,
    ApexLegend,
    ApexTooltip,
} from 'ng-apexcharts';
import {
    colorAec1BlueLight,
    colorAec2BlueDark,
    colorAec3Green,
    colorAec4Orange,
    colorAec5Brown,
    colorAec6Gray,
    colorAec7RedLight,
    colorAec8RedDark,
    colorBlack,
} from 'src/app/shared/all.constants';
import {
    OPERATING_ACCOUNT_KPIS,
    OPERATING_COSTS_EVOLUTION_CHART,
    OPERATING_MARGIN_RATE_EVOLUTION_CHART,
    OPERATING_PRODUCTS_EVOLUTION_CHART,
    OPERATING_RESULT_EVOLUTION_CHART,
} from '../../assets/keys';

const KEYS = [
    OPERATING_ACCOUNT_KPIS,
    OPERATING_PRODUCTS_EVOLUTION_CHART,
    OPERATING_COSTS_EVOLUTION_CHART,
    OPERATING_RESULT_EVOLUTION_CHART,
    OPERATING_MARGIN_RATE_EVOLUTION_CHART,
];

interface KpiData {
    organizationId?: string;
    year?: string;
    error?: boolean;

    totalOperatingRevenueAmount?: number;
    totalOperatingRevenueAmountEvolution?: number;

    totalOperatingExpensesAmount?: number;
    totalOperatingExpensesAmountEvolution?: number;

    actualConcessionMarginRate?: number;
    actualConcessionMarginRateEvolution?: number;

    displayedConcessionMarginRate?: number;
    displayedConcessionMarginRateEvolution?: number;

    mainKeyDistributedAmounts?: number;
}

interface OperatingProductsEvolutionChartData {
    organizationId?: string;
    year?: string;
    xYear?: number;
    connectionFees?: number;
    storedAndCapitalizedProduction?: number;
    transportRevenues?: number;
    depreciationReversals?: number;
    otherOperatingIncome?: number;
    totalIncome?: number;
    error?: boolean;
}

interface OperatingCostsEvolutionChartData {
    organizationId?: string;
    xYear?: number;
    year?: string;
    otherCosts?: number;
    upstreamNetworkAccessAndLossPurchases?: number;
    centralCosts?: number;
    taxesAndContributions?: number;
    personnelCosts?: number;
    depreciationAndProvisions?: number;
    totalCosts?: number;
    error?: boolean;
}

interface OperatingResultEvolutionChartData {
    organizationId?: string;
    year?: string;
    xYear?: number;
    displayedResult?: number;
    actualResult?: number;
    error?: boolean;
}

interface OperatingMarginRateEvolutionChartData {
    organizationId?: string;
    year?: string;
    xYear?: number;
    actualMarginRate?: number;
    displayedMarginRate?: number;
    error?: boolean;
}

@UntilDestroy()
@Component({
    selector: 'app-operating-account',
    templateUrl: './operating-account.component.html',
    styleUrls: ['./operating-account.component.scss'],
})
export class OperatingAccountComponent extends GenericComponent {
    public texts = CC_CONSTANTS.operatingAccount.texts;
    public operatingProductsChartSeries: ApexAxisChartSeries = [];
    public operatingCostsChartSeries: ApexAxisChartSeries = [];
    public operatingResultChartSeries: ApexAxisChartSeries = [];
    public operatingMarginRateChartSeries: ApexAxisChartSeries = [];
    public operatingProductsDataLabels: ApexDataLabels = {
        enabled: true,
        enabledOnSeries: [],
        style: this.setDataLabelsStyleColors(this.texts.operatingProductsEvolutionChart.totalTitle),
    };
    public operatingCostsDataLabels: ApexDataLabels = {
        enabled: true,
        enabledOnSeries: [],
        style: this.setDataLabelsStyleColors(this.texts.operatingCostsEvolutionChart.totalTitle),
    };
    public operatingResultDataLabels: ApexDataLabels = { enabled: true, enabledOnSeries: [] };
    public operatingMarginRateDataLabels: ApexDataLabels = { enabled: true, enabledOnSeries: [] };
    public operatingResultChartOptions: ApexOptions = {};

    public kpis: Signal<KpiData[]> = this.store.selectSignal(
        ConcessioncontrolState.getDataByKey(OPERATING_ACCOUNT_KPIS),
    ) as Signal<KpiData[]>;

    public operatingCostsChartData: Signal<OperatingCostsEvolutionChartData[]> =
        this.store.selectSignal(
            ConcessioncontrolState.getDataByKey(OPERATING_COSTS_EVOLUTION_CHART),
        ) as Signal<OperatingCostsEvolutionChartData[]>;

    public operatingProductsChartData: Signal<OperatingProductsEvolutionChartData[]> =
        this.store.selectSignal(
            ConcessioncontrolState.getDataByKey(OPERATING_PRODUCTS_EVOLUTION_CHART),
        ) as Signal<OperatingProductsEvolutionChartData[]>;

    public operatingResultChartData: Signal<OperatingResultEvolutionChartData[]> =
        this.store.selectSignal(
            ConcessioncontrolState.getDataByKey(OPERATING_RESULT_EVOLUTION_CHART),
        ) as Signal<OperatingResultEvolutionChartData[]>;

    public operatingMarginRateChartData: Signal<OperatingMarginRateEvolutionChartData[]> =
        this.store.selectSignal(
            ConcessioncontrolState.getDataByKey(OPERATING_MARGIN_RATE_EVOLUTION_CHART),
        ) as Signal<OperatingMarginRateEvolutionChartData[]>;

    public operatingProductsYaxis: ApexYAxis[] = [
        {
            seriesName: 'Serie 1',
            min: 0,
            title: {
                text: this.texts.operatingProductsEvolutionChart.yaxisTitleLeft,
            },
        },
    ];
    public operatingCostsYaxis: ApexYAxis[] = [
        {
            seriesName: 'Serie 1',
            min: 0,
            title: {
                text: this.texts.operatingCostsEvolutionChart.yaxisTitleLeft,
            },
        },
    ];
    public operatingResultYaxis: ApexYAxis[] = [
        {
            seriesName: 'Serie 1',
            title: {
                text: this.texts.operatingResultEvolutionChart.yaxisTitleLeft,
            },
        },
    ];
    public operatingMarginRateYaxis: ApexYAxis[] = [
        {
            seriesName: 'Serie 1',
            title: {
                text: this.texts.operatingMarginRateEvolutionChart.yaxisTitleLeft,
            },
        },
    ];
    public operatingProductsChartError: boolean = true;
    public operatingCostsChartError: boolean = true;
    public operatingResultChartError: boolean = true;
    public operatingMarginRateChartError: boolean = true;

    public operatingResultEvolutionGrid: ApexGrid = { padding: { left: 30 } };
    public operatingMarginRateGrid: ApexGrid = { padding: { left: 30 } };

    public operatingResultChartAnnotations: ApexAnnotations = {
        yaxis: [
            {
                y: 0,
                borderColor: colorBlack,
                borderWidth: 1,
                strokeDashArray: 0,
            },
        ],
    };

    public operatingProductsLegend: ApexLegend = {
        markers: {
            fillColors: [
                colorAec7RedLight,
                colorAec5Brown,
                colorAec1BlueLight,
                colorAec3Green,
                colorAec6Gray,
                colorBlack,
            ],
        },
    };
    public operatingCostsLegend: ApexLegend = {
        markers: {
            fillColors: [
                colorAec7RedLight,
                colorAec1BlueLight,
                colorAec3Green,
                colorAec6Gray,
                colorAec4Orange,
                colorAec5Brown,
                colorBlack,
            ],
        },
    };

    public operatingProductsCustomTooltip: ApexTooltip['custom'];
    public operatingCostsCustomTooltip: ApexTooltip['custom'];

    constructor(
        private readonly route: ActivatedRoute,
        private readonly sidenavService: SidenavService,
        private readonly store: Store,
    ) {
        super(route, sidenavService, store);

        effect(() => {
            const operatingProductsDataList = this.operatingProductsChartData();
            const operatingCostsDataList = this.operatingCostsChartData();
            const operatingResultDataList = this.operatingResultChartData();
            const operatingMarginRateDataList = this.operatingMarginRateChartData();
            this.operatingProductsChartError = this.checkForError(operatingProductsDataList, [
                'xYear',
                'connectionFees',
                'storedAndCapitalizedProduction',
                'transportRevenues',
                'depreciationReversals',
                'otherOperatingIncome',
            ]);
            this.operatingCostsChartError = this.checkForError(operatingCostsDataList, [
                'xYear',
                'otherCosts',
                'upstreamNetworkAccessAndLossPurchases',
                'centralCosts',
                'taxesAndContributions',
                'personnelCosts',
                'depreciationAndProvisions',
            ]);
            this.operatingResultChartError = this.checkForError(operatingResultDataList, [
                'xYear',
                'displayedResult',
                'actualResult',
            ]);
            this.operatingMarginRateChartError = this.checkForError(operatingMarginRateDataList, [
                'xYear',
                'actualMarginRate',
                'displayedMarginRate',
            ]);

            if (!this.operatingProductsChartError) {
                const totales = this.getTotales(operatingProductsDataList, [
                    'connectionFees',
                    'storedAndCapitalizedProduction',
                    'transportRevenues',
                    'depreciationReversals',
                    'otherOperatingIncome',
                ]);
                this.operatingProductsChartSeries = [
                    {
                        name: this.texts.operatingProductsEvolutionChart.connectionFees,
                        type: 'column',
                        data: operatingProductsDataList.map((data) => ({
                            x: data.xYear,
                            y: data.connectionFees,
                        })),
                        color: colorAec7RedLight,
                    },
                    {
                        name: this.texts.operatingProductsEvolutionChart
                            .storedAndCapitalizedProduction,
                        type: 'column',
                        data: operatingProductsDataList.map((data) => ({
                            x: data.xYear,
                            y: data.storedAndCapitalizedProduction,
                        })),
                        color: colorAec5Brown,
                    },
                    {
                        name: this.texts.operatingProductsEvolutionChart.transportRevenues,
                        type: 'column',
                        data: operatingProductsDataList.map((data) => ({
                            x: data.xYear,
                            y: data.transportRevenues,
                        })),
                        color: colorAec1BlueLight,
                    },
                    {
                        name: this.texts.operatingProductsEvolutionChart.depreciationReversals,
                        type: 'column',
                        data: operatingProductsDataList.map((data) => ({
                            x: data.xYear,
                            y: data.depreciationReversals,
                        })),
                        color: colorAec3Green,
                    },
                    {
                        name: this.texts.operatingProductsEvolutionChart.otherOperatingIncome,
                        type: 'column',
                        data: operatingProductsDataList.map((data) => ({
                            x: data.xYear,
                            y: data.otherOperatingIncome,
                        })),
                        color: colorAec6Gray,
                    },
                    {
                        name: this.texts.operatingProductsEvolutionChart.totalTitle,
                        type: 'line',
                        data: operatingProductsDataList.map((data, index) => ({
                            x: data.xYear,
                            y: totales[index],
                        })),
                        color: 'transparent',
                    },
                ];
                this.operatingProductsDataLabels.enabledOnSeries = [0, 1, 2, 3, 4, 5];
            }

            if (!this.operatingCostsChartError) {
                const totales = this.getTotales(operatingCostsDataList, [
                    'otherCosts',
                    'upstreamNetworkAccessAndLossPurchases',
                    'centralCosts',
                    'taxesAndContributions',
                    'personnelCosts',
                    'depreciationAndProvisions',
                ]);
                this.operatingCostsChartSeries = [
                    {
                        name: this.texts.operatingCostsEvolutionChart
                            .upstreamNetworkAccessAndLossPurchases,
                        type: 'column',
                        data: operatingCostsDataList.map((data) => ({
                            x: data.xYear,
                            y: data.upstreamNetworkAccessAndLossPurchases,
                        })),
                        color: colorAec7RedLight,
                    },
                    {
                        name: this.texts.operatingCostsEvolutionChart.personnelCosts,
                        type: 'column',
                        data: operatingCostsDataList.map((data) => ({
                            x: data.xYear,
                            y: data.personnelCosts,
                        })),
                        color: colorAec1BlueLight,
                    },
                    {
                        name: this.texts.operatingCostsEvolutionChart.taxesAndContributions,
                        type: 'column',
                        data: operatingCostsDataList.map((data) => ({
                            x: data.xYear,
                            y: data.taxesAndContributions,
                        })),
                        color: colorAec3Green,
                    },
                    {
                        name: this.texts.operatingCostsEvolutionChart.otherCosts,
                        type: 'column',
                        data: operatingCostsDataList.map((data) => ({
                            x: data.xYear,
                            y: data.otherCosts,
                        })),
                        color: colorAec6Gray,
                    },
                    {
                        name: this.texts.operatingCostsEvolutionChart.centralCosts,
                        type: 'column',
                        data: operatingCostsDataList.map((data) => ({
                            x: data.xYear,
                            y: data.centralCosts,
                        })),
                        color: colorAec4Orange,
                    },
                    {
                        name: this.texts.operatingCostsEvolutionChart.depreciationAndProvisions,
                        type: 'column',
                        data: operatingCostsDataList.map((data) => ({
                            x: data.xYear,
                            y: data.depreciationAndProvisions,
                        })),
                        color: colorAec5Brown,
                    },
                    {
                        name: this.texts.operatingCostsEvolutionChart.totalTitle,
                        type: 'line',
                        data: operatingCostsDataList.map((data, index) => ({
                            x: data.xYear,
                            y: totales[index],
                        })),
                        color: 'transparent',
                    },
                ];
                this.operatingCostsDataLabels.enabledOnSeries = [0, 1, 2, 3, 4, 5, 6];
            }

            if (!this.operatingResultChartError) {
                this.operatingResultChartSeries = [
                    {
                        name: this.texts.operatingResultEvolutionChart.actualResult,
                        type: 'line',
                        data: operatingResultDataList.map((data) => ({
                            x: data.xYear,
                            y: data.actualResult,
                        })),
                        color: colorAec1BlueLight,
                    },
                    {
                        name: this.texts.operatingResultEvolutionChart.displayedResult,
                        type: 'line',
                        data: operatingResultDataList.map((data) => ({
                            x: data.xYear,
                            y: data.displayedResult,
                        })),
                        color: colorAec8RedDark,
                    },
                ];
                this.operatingResultDataLabels.enabledOnSeries = [0, 1];
            }

            if (!this.operatingMarginRateChartError) {
                this.operatingMarginRateChartSeries = [
                    {
                        name: this.texts.operatingMarginRateEvolutionChart.actualMarginRate,
                        type: 'line',
                        data: operatingMarginRateDataList.map((data) => ({
                            x: data.xYear,
                            y: data.actualMarginRate,
                        })),
                        color: colorAec1BlueLight,
                    },
                    {
                        name: this.texts.operatingMarginRateEvolutionChart.displayedMarginRate,
                        type: 'line',
                        data: operatingMarginRateDataList.map((data) => ({
                            x: data.xYear,
                            y: data.displayedMarginRate,
                        })),
                        color: colorAec8RedDark,
                    },
                ];
                this.operatingMarginRateDataLabels.enabledOnSeries = [0, 1];
            }
        });
    }

    ngOnInit() {
        // Every time currentOrganization or currentYear changes, we tell the store he has to ask for the data
        combineLatest([this._currentOrganization$, this._currentYear$])
            .pipe(untilDestroyed(this), distinctUntilChanged())
            .subscribe(() => {
                this.store.dispatch(new PrepareDataFromKeys({ keys: KEYS }));
            });

        this.operatingProductsCustomTooltip = this.setOperatingProductsCustomTooltip();
        this.operatingCostsCustomTooltip = this.setOperatingCostsCustomTooltip();
    }

    private setOperatingProductsCustomTooltip(): ApexTooltip['custom'] {
        return ({ data, seriesIndex, dataPointIndex, w }) => {
            const collapsedSeriesIndices = w.globals.collapsedSeriesIndices;

            const labels = w.globals.labels;
            const title = labels[dataPointIndex];

            const precision = this.texts.operatingProductsEvolutionChart.precision;

            const connectionFeesSerie = w.globals.initialSeries[0];
            const {
                name: connectionFeesName,
                color: connectionFeesColor,
                valueStringified: connectionFeesStringified,
            } = this.setCustomTooltipData(precision, connectionFeesSerie, dataPointIndex);

            const storedAndCapitalizedProductionSerie = w.globals.initialSeries[1];
            const {
                name: storedAndCapitalizedProductionName,
                color: storedAndCapitalizedProductionColor,
                valueStringified: storedAndCapitalizedProductionStringified,
            } = this.setCustomTooltipData(
                precision,
                storedAndCapitalizedProductionSerie,
                dataPointIndex,
            );

            const transportRevenuesSerie = w.globals.initialSeries[2];
            const {
                name: transportRevenuesName,
                color: transportRevenuesColor,
                valueStringified: transportRevenuesStringified,
            } = this.setCustomTooltipData(precision, transportRevenuesSerie, dataPointIndex);

            const depreciationReversalsSerie = w.globals.initialSeries[3];
            const {
                name: depreciationReversalsName,
                color: depreciationReversalsColor,
                valueStringified: depreciationReversalsStringified,
            } = this.setCustomTooltipData(precision, depreciationReversalsSerie, dataPointIndex);

            const otherOperatingIncomeSerie = w.globals.initialSeries[4];
            const {
                name: otherOperatingIncomeName,
                color: otherOperatingIncomeColor,
                valueStringified: otherOperatingIncomeStringified,
            } = this.setCustomTooltipData(precision, otherOperatingIncomeSerie, dataPointIndex);

            const totalesSerie = w.globals.initialSeries[5];
            const {
                name: totalesName,
                color: totalesColor,
                valueStringified: totalesStringified,
            } = this.setCustomTooltipData(precision, totalesSerie, dataPointIndex);

            const dataTooltips = [
                {
                    color: connectionFeesColor,
                    name: connectionFeesName,
                    value: connectionFeesStringified + ' M€',
                },
                {
                    color: storedAndCapitalizedProductionColor,
                    name: storedAndCapitalizedProductionName,
                    value: storedAndCapitalizedProductionStringified + ' M€',
                },
                {
                    color: transportRevenuesColor,
                    name: transportRevenuesName,
                    value: transportRevenuesStringified + ' M€',
                },
                {
                    color: depreciationReversalsColor,
                    name: depreciationReversalsName,
                    value: depreciationReversalsStringified + ' M€',
                },
                {
                    color: otherOperatingIncomeColor,
                    name: otherOperatingIncomeName,
                    value: otherOperatingIncomeStringified + ' M€',
                },
                {
                    color: colorBlack,
                    name: totalesName,
                    value: totalesStringified + ' M€',
                },
            ];
            const html = this.setCustomTooltip(collapsedSeriesIndices, title, dataTooltips);
            return html;
        };
    }

    private setOperatingCostsCustomTooltip(): ApexTooltip['custom'] {
        return ({ data, seriesIndex, dataPointIndex, w }) => {
            const collapsedSeriesIndices = w.globals.collapsedSeriesIndices;

            const labels = w.globals.labels;
            const title = labels[dataPointIndex];

            const precision = this.texts.operatingProductsEvolutionChart.precision;

            const upstreamNetworkAccessAndLossPurchasesSerie = w.globals.initialSeries[0];
            const {
                name: upstreamNetworkAccessAndLossPurchasesName,
                color: upstreamNetworkAccessAndLossPurchasesColor,
                valueStringified: upstreamNetworkAccessAndLossPurchasesStringified,
            } = this.setCustomTooltipData(
                precision,
                upstreamNetworkAccessAndLossPurchasesSerie,
                dataPointIndex,
            );

            const personnelCostsSerie = w.globals.initialSeries[1];
            const {
                name: personnelCostsName,
                color: personnelCostsColor,
                valueStringified: personnelCostsStringified,
            } = this.setCustomTooltipData(precision, personnelCostsSerie, dataPointIndex);

            const taxesAndContributionsSerie = w.globals.initialSeries[2];
            const {
                name: taxesAndContributionsName,
                color: taxesAndContributionsColor,
                valueStringified: taxesAndContributionsStringified,
            } = this.setCustomTooltipData(precision, taxesAndContributionsSerie, dataPointIndex);

            const otherCostsSerie = w.globals.initialSeries[3];
            const {
                name: otherCostsName,
                color: otherCostsColor,
                valueStringified: otherCostsStringified,
            } = this.setCustomTooltipData(precision, otherCostsSerie, dataPointIndex);

            const centralCostsSerie = w.globals.initialSeries[4];
            const {
                name: centralCostsName,
                color: centralCostsColor,
                valueStringified: centralCostsStringified,
            } = this.setCustomTooltipData(precision, centralCostsSerie, dataPointIndex);

            const depreciationAndProvisionsSerie = w.globals.initialSeries[5];
            const {
                name: depreciationAndProvisionsName,
                color: depreciationAndProvisionsColor,
                valueStringified: depreciationAndProvisionsStringified,
            } = this.setCustomTooltipData(
                precision,
                depreciationAndProvisionsSerie,
                dataPointIndex,
            );

            const totalesSerie = w.globals.initialSeries[6];
            const {
                name: totalesName,
                color: totalesColor,
                valueStringified: totalesStringified,
            } = this.setCustomTooltipData(precision, totalesSerie, dataPointIndex);

            const dataTooltips = [
                {
                    color: upstreamNetworkAccessAndLossPurchasesColor,
                    name: upstreamNetworkAccessAndLossPurchasesName,
                    value: upstreamNetworkAccessAndLossPurchasesStringified + ' M€',
                },
                {
                    color: personnelCostsColor,
                    name: personnelCostsName,
                    value: personnelCostsStringified + ' M€',
                },
                {
                    color: taxesAndContributionsColor,
                    name: taxesAndContributionsName,
                    value: taxesAndContributionsStringified + ' M€',
                },
                {
                    color: otherCostsColor,
                    name: otherCostsName,
                    value: otherCostsStringified + ' M€',
                },
                {
                    color: centralCostsColor,
                    name: centralCostsName,
                    value: centralCostsStringified + ' M€',
                },
                {
                    color: depreciationAndProvisionsColor,
                    name: depreciationAndProvisionsName,
                    value: depreciationAndProvisionsStringified + ' M€',
                },
                {
                    color: colorBlack,
                    name: totalesName,
                    value: totalesStringified + ' M€',
                },
            ];
            const html = this.setCustomTooltip(collapsedSeriesIndices, title, dataTooltips);
            return html;
        };
    }
}
